/**
 * Centralized logging service for the application
 * Provides consistent logging with levels and structured data
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  context?: string;
  data?: Record<string, unknown>;
  userId?: string;
  propertyId?: string;
}

export interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enablePersistence: boolean;
  maxStorageEntries: number;
}

class Logger {
  private config: LoggerConfig;
  private logHistory: LogEntry[] = [];
  private readonly STORAGE_KEY = 'smartrate-logs';

  private readonly logLevels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      minLevel: import.meta.env.DEV ? 'debug' : 'info',
      enableConsole: true,
      enablePersistence: import.meta.env.DEV,
      maxStorageEntries: 1000,
      ...config,
    };

    this.loadStoredLogs();
  }

  private shouldLog(level: LogLevel): boolean {
    return this.logLevels[level] >= this.logLevels[this.config.minLevel];
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): LogEntry {
    return {
      timestamp: new Date(),
      level,
      message,
      context,
      data,
      userId: this.getCurrentUserId(),
      propertyId: this.getCurrentPropertyId(),
    };
  }

  private getCurrentUserId(): string | undefined {
    // In a real app, get from auth context
    return 'current-user-id';
  }

  private getCurrentPropertyId(): string | undefined {
    // In a real app, get from app context
    return 'current-property-id';
  }

  private formatConsoleMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString();
    const context = entry.context ? `[${entry.context}]` : '';
    const data = entry.data ? JSON.stringify(entry.data, null, 2) : '';

    return `${timestamp} ${context} ${entry.message} ${data}`.trim();
  }

  private logToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const message = this.formatConsoleMessage(entry);

    switch (entry.level) {
      case 'debug':
        console.debug(message);
        break;
      case 'info':
        console.log(message);
        break;
      case 'warn':
        console.warn(message);
        break;
      case 'error':
        console.error(message);
        break;
    }
  }

  private persistLog(entry: LogEntry): void {
    if (!this.config.enablePersistence) return;

    this.logHistory.push(entry);

    // Keep only the most recent entries
    if (this.logHistory.length > this.config.maxStorageEntries) {
      this.logHistory = this.logHistory.slice(-this.config.maxStorageEntries);
    }

    try {
      const serializedLogs = JSON.stringify(this.logHistory);
      localStorage.setItem(this.STORAGE_KEY, serializedLogs);
    } catch (error) {
      console.warn('Failed to persist logs:', error);
    }
  }

  private loadStoredLogs(): void {
    if (!this.config.enablePersistence) return;

    try {
      const storedLogs = localStorage.getItem(this.STORAGE_KEY);
      if (storedLogs) {
        const parsedLogs = JSON.parse(storedLogs) as Array<
          Omit<LogEntry, 'timestamp'> & { timestamp: string }
        >;
        this.logHistory = parsedLogs.map((log) => ({
          ...log,
          timestamp: new Date(log.timestamp),
        }));
      }
    } catch (error) {
      console.warn('Failed to load stored logs:', error);
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, context, data);

    this.logToConsole(entry);
    this.persistLog(entry);
  }

  // Public logging methods
  debug(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log('debug', message, context, data);
  }

  info(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log('info', message, context, data);
  }

  warn(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log('warn', message, context, data);
  }

  error(
    message: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.log('error', message, context, data);
  }

  // Utility methods
  getLogs(level?: LogLevel): LogEntry[] {
    if (!level) return [...this.logHistory];

    return this.logHistory.filter((entry) => entry.level === level);
  }

  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logHistory.slice(-count);
  }

  clearLogs(): void {
    this.logHistory = [];
    if (this.config.enablePersistence) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // Performance logging
  time(label: string): void {
    if (import.meta.env.DEV) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (import.meta.env.DEV) {
      console.timeEnd(label);
    }
  }

  // Database operation logging
  database(
    operation: string,
    table: string,
    data?: Record<string, unknown>
  ): void {
    this.debug(`Database ${operation}`, 'database', { table, ...data });
  }

  // API call logging
  api(
    method: string,
    url: string,
    status?: number,
    data?: Record<string, unknown>
  ): void {
    const level = status && status >= 400 ? 'error' : 'info';
    this.log(level, `${method} ${url}`, 'api', { status, ...data });
  }

  // User action logging
  userAction(
    action: string,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.info(`User action: ${action}`, context, data);
  }

  // Error with stack trace
  errorWithStack(
    error: Error,
    context?: string,
    data?: Record<string, unknown>
  ): void {
    this.error(error.message, context, {
      stack: error.stack,
      name: error.name,
      ...data,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions for direct use
export const log = {
  debug: (message: string, context?: string, data?: Record<string, unknown>) =>
    logger.debug(message, context, data),
  info: (message: string, context?: string, data?: Record<string, unknown>) =>
    logger.info(message, context, data),
  warn: (message: string, context?: string, data?: Record<string, unknown>) =>
    logger.warn(message, context, data),
  error: (message: string, context?: string, data?: Record<string, unknown>) =>
    logger.error(message, context, data),
  database: (
    operation: string,
    table: string,
    data?: Record<string, unknown>
  ) => logger.database(operation, table, data),
  api: (
    method: string,
    url: string,
    status?: number,
    data?: Record<string, unknown>
  ) => logger.api(method, url, status, data),
  userAction: (
    action: string,
    context?: string,
    data?: Record<string, unknown>
  ) => logger.userAction(action, context, data),
  errorWithStack: (
    error: Error,
    context?: string,
    data?: Record<string, unknown>
  ) => logger.errorWithStack(error, context, data),
};

export default logger;
